import * as THREE from 'three';
import FVector from '@data/geometry/FVector';
import { isPointInPolygon } from '@data/geometry/Functions';
import { GeometryUtils } from '@data/geometry/GeometryUtils';
import { FloorBasedFacadeState } from '@visualizer/types';
import { isEdgeBlocked } from '../FacadeVisibility';
import { Component, FacadeComponentContext } from './Component';

// =====================================
// TYPES
// =====================================

export type PvPanelsDerivedData = {
    alphaDeg: number;
    heightB: number;
};

export type PvPanelsConfig = {
    panelHeight: number;
    panelLength: number;
    panelOffset: number;
    connectCorners: boolean;
    closed: boolean;
    panelAngle: number;
    useCustomAngle: boolean;
    facadeConfig?: FloorBasedFacadeState;
};

export class PvPanelsComponent extends Component {
    // =====================================
    // CLASS FIELDS
    // =====================================

    private avgDirection = new FVector();
    private perimeter: FVector[];
    private visibilityLoops: FVector[][];
    private panelHeight: number;
    private panelLength: number;
    private panelOffset: number;
    private connectCorners: boolean;
    private closed: boolean;
    private renderWhenBlocked: boolean;
    private requireActiveSensors: boolean;
    private edgeIndex: number;
    private renderedIndices: Set<number>;
    private useCustomAngle: boolean;
    private panelAngle: number;
    private derivedData?: PvPanelsDerivedData;
    private facadeConfig?: FloorBasedFacadeState;

    getDerivedData(): PvPanelsDerivedData | undefined {
        return this.derivedData;
    }

    constructor(context: FacadeComponentContext, config: PvPanelsConfig) {
        super(context);
        this.perimeter = context.perimeter;
        this.visibilityLoops = context.visibilityLoops;
        this.panelHeight = config.panelHeight;
        this.panelLength = config.panelLength;
        this.panelOffset = THREE.MathUtils.clamp(config.panelOffset, 0, 4);
        this.connectCorners = config.connectCorners;
        this.closed = config.closed;
        this.renderWhenBlocked = context.renderWhenBlocked;
        this.requireActiveSensors = context.requireActiveSensors;
        this.edgeIndex = context.edgeIndex;
        this.renderedIndices = context.renderedIndices;
        this.panelAngle = THREE.MathUtils.degToRad(config.panelAngle);
        this.useCustomAngle = config.useCustomAngle;
        this.facadeConfig = config.facadeConfig;
    }

    // =====================================
    // HELPER FUNCTIONS
    // =====================================

    private getTiltAngleRad(): number {
        if (this.useCustomAngle) return this.panelAngle;

        const normalized = new THREE.Vector3(this.avgDirection.x, this.avgDirection.y, this.avgDirection.z).normalize();
        return Math.PI / 2 - Math.abs(Math.asin(normalized.z));
    }

    private updateDerivedData(): void {
        const tiltAngleRad = this.getTiltAngleRad();
        this.derivedData = {
            alphaDeg: THREE.MathUtils.radToDeg(tiltAngleRad),
            heightB: Math.abs(this.panelLength * Math.sin(tiltAngleRad)),
        };
    }

    private syncFacadeConfig(): void {
        if (!this.facadeConfig) return;
        if (!this.facadeConfig.verticalCustomAngle) {
            this.facadeConfig.verticalAngle.value = this.getDerivedData()?.alphaDeg ?? this.facadeConfig.verticalAngle.value;
        }
        this.facadeConfig.verticalHeightB = this.getDerivedData()?.heightB ?? this.facadeConfig.verticalHeightB;
    }

    private findVertexIndex(poly: THREE.Vector2[], v: THREE.Vector2, eps = 1e-8): number {
        for (let i = 0; i < poly.length; i++) {
            if (poly[i].distanceToSquared(v) < eps * eps) return i;
        }
        return -1;
    }

    // Connect only obtuse corners:
    private isObtuseCorner(vertexIndex: number): boolean {
        const count = this.perimeter.length;
        if (count < 3 || vertexIndex < 0 || vertexIndex >= count) return false;

        const prev = this.perimeter[(vertexIndex - 1 + count) % count];
        const vertex = this.perimeter[vertexIndex];
        const next = this.perimeter[(vertexIndex + 1) % count];

        const incoming = new THREE.Vector2(vertex.x - prev.x, vertex.y - prev.y);
        const outgoing = new THREE.Vector2(next.x - vertex.x, next.y - vertex.y);
        const incomingLength = incoming.length();
        const outgoingLength = outgoing.length();
        if (incomingLength <= 1e-9 || outgoingLength <= 1e-9) return false;

        const incomingNorm = incoming.clone().divideScalar(incomingLength);
        const outgoingNorm = outgoing.clone().divideScalar(outgoingLength);
        const unsignedAngle = incomingNorm.angleTo(outgoingNorm);
        const crossZ = incomingNorm.x * outgoingNorm.y - incomingNorm.y * outgoingNorm.x;
        const isCCW = GeometryUtils.polygonIsCCW(this.perimeter.map((p) => new THREE.Vector2(p.x, p.y)));
        const isReflex = isCCW ? crossZ < 0 : crossZ > 0;
        const interiorAngle = isReflex ? Math.PI * 2 - unsignedAngle : unsignedAngle;

        return interiorAngle > Math.PI / 2;
    }

    private computePushOutForEdge(a: FVector, b: FVector, originalA: FVector = a, originalB: FVector = b): [THREE.Vector3, THREE.Vector3] {
        // Step 1:
        const poly2 = this.perimeter.map((p) => new THREE.Vector2(p.x, p.y));

        const a2 = new THREE.Vector2(originalA.x, originalA.y);
        const b2 = new THREE.Vector2(originalB.x, originalB.y);

        const ia = this.findVertexIndex(poly2, a2);
        const ib = this.findVertexIndex(poly2, b2);

        if (ia < 0 || ib < 0) {
            throw new Error('Facade endpoints are not polygon vertices');
        }

        // Step 2:
        const n = poly2.length;
        // If reversed, swap a and b
        if (ia === (ib + 1) % n) {
            // swap a and b
            const tmp = a.clone();
            a.copy(b);
            b.copy(tmp);
        }

        const edgeVec = new THREE.Vector3(b.x - a.x, b.y - a.y, 0).normalize();
        // Left perpendicular of edge direction
        let perpVec = new THREE.Vector3(-edgeVec.y, edgeVec.x, 0);

        // For CCW polygons: interior is on the left -> outward is right
        if (GeometryUtils.polygonIsCCW(poly2)) {
            perpVec.multiplyScalar(-1);
        }

        perpVec.normalize();

        const tiltAngleRad = this.getTiltAngleRad();
        let dir: THREE.Vector3;

        // Tilt by angle:
        if (this.useCustomAngle) {
            // MANUAL MODE: Rotate based on panelAngle:
            const horizontalDir = perpVec.clone().normalize();

            dir = horizontalDir
                .clone()
                .multiplyScalar(Math.cos(tiltAngleRad))
                .add(new THREE.Vector3(0, 0, -Math.sin(tiltAngleRad)))
                .normalize();
        } else {
            // AUTO MODE: derive angle from avgDirection z Component:
            const normalized = new THREE.Vector3(this.avgDirection.x, this.avgDirection.y, this.avgDirection.z).normalize();
            const alphaRad = Math.abs(Math.asin(normalized.z)) - Math.PI / 2;

            dir = perpVec
                .clone()
                .multiplyScalar(Math.cos(alphaRad))
                .add(new THREE.Vector3(0, 0, Math.sin(alphaRad)))
                .normalize();
        }

        // Apply length (L) to the facade:
        const pushOutVec = dir.multiplyScalar(this.panelLength);

        // Apply height (a) to the facade:
        const aOut = new THREE.Vector3(a.x + pushOutVec.x, a.y + pushOutVec.y, a.z + pushOutVec.z + this.panelHeight);
        const bOut = new THREE.Vector3(b.x + pushOutVec.x, b.y + pushOutVec.y, b.z + pushOutVec.z + this.panelHeight);

        // Compute b & alpha:
        const horizontalMag = Math.sqrt(dir.x * dir.x + dir.y * dir.y);
        const alphaRad = Math.atan2(dir.z, horizontalMag);
        const alphaDeg = Math.abs(THREE.MathUtils.radToDeg(alphaRad));
        const heightB = Math.abs(pushOutVec.z);

        this.derivedData = {
            alphaDeg,
            heightB,
        };

        return [aOut, bOut];
    }

    // Function to offset the PV Panel by a certain distance (panelOffset):
    private getOffsetEdge(a: FVector, b: FVector): [FVector, FVector] {
        const edgeDir = new THREE.Vector3(b.x - a.x, b.y - a.y, 0).normalize();
        let outwardPerp = new THREE.Vector3(-edgeDir.y, edgeDir.x, 0);
        if (GeometryUtils.polygonIsCCW(this.perimeter.map((p) => new THREE.Vector2(p.x, p.y)))) {
            outwardPerp.multiplyScalar(-1);
        }
        outwardPerp.normalize().multiplyScalar(this.panelOffset);
        return [new FVector(a.x + outwardPerp.x, a.y + outwardPerp.y, a.z), new FVector(b.x + outwardPerp.x, b.y + outwardPerp.y, b.z)];
    }

    private addFace(shadingGroup: THREE.Group, points: FVector[], indices: number[]): void {
        const geom = new THREE.BufferGeometry().setFromPoints(points.map((p) => new THREE.Vector3(p.x, p.y, p.z)));
        geom.setIndex(indices);
        geom.computeVertexNormals();

        const mesh = new THREE.Mesh(geom, this.material);
        const edges = new THREE.LineSegments(new THREE.EdgesGeometry(geom, 0.01), PvPanelsComponent.edgeMaterial);
        shadingGroup.add(mesh, edges);
    }

    // Function for connected panels, intersects two lines and returns intersection point:
    private intersectLines(
        enabled: boolean,
        currA: THREE.Vector2 | null,
        currB: THREE.Vector2 | null,
        nextA: THREE.Vector2,
        nextB: THREE.Vector2
    ): THREE.Vector2 | null {
        if (!enabled || !currA || !currB) return null;
        return GeometryUtils.intersect2D(new THREE.Vector2(currA.x, currA.y), new THREE.Vector2(currB.x, currB.y), nextA, nextB);
    }

    // =====================================
    // MAIN GENERATION FUNCTION
    // =====================================

    override build(): THREE.Group {
        const shadingGroup = new THREE.Group();

        // If required, only render if at least one sensor is active in the facade:
        if (this.requireActiveSensors && !this.sensors.some((sensor) => sensor.getScalarValue() > 0)) return shadingGroup;

        this.avgDirection = this.getFacadeDirection()?.clone() ?? new FVector();
        this.updateDerivedData();
        const avgDirXY = new THREE.Vector3(this.avgDirection.x, this.avgDirection.y, 0).normalize();

        // 1. Skip rendering if this edge is blocked and current coverage mode does not render blocked edges:
        if (isEdgeBlocked(this.p1, this.p2, this.perimeter, this.avgDirection, this.visibilityLoops) && !this.renderWhenBlocked) {
            this.syncFacadeConfig();
            return shadingGroup;
        }

        // 2. Compute pushout points of this facade (they could be offsetted as well):
        const [p1BaseEdge, p2BaseEdge] = this.getOffsetEdge(this.p1, this.p2);
        const [p1Out, p2Out] = this.computePushOutForEdge(p1BaseEdge, p2BaseEdge, this.p1, this.p2);
        const p1Base = new FVector(p1BaseEdge.x, p1BaseEdge.y, p1BaseEdge.z + this.panelHeight);
        const p2Base = new FVector(p2BaseEdge.x, p2BaseEdge.y, p2BaseEdge.z + this.panelHeight);

        // 3. Resolve whether previous/next corners should be connected:
        const idx = this.perimeter.findIndex((v) => v.x === this.p1.x && v.y === this.p1.y);
        const idx2 = this.perimeter.findIndex((v) => v.x === this.p2.x && v.y === this.p2.y);
        const connectPrevCorner = this.connectCorners || this.isObtuseCorner(idx);
        const connectNextCorner = this.connectCorners || this.isObtuseCorner(idx2);

        if (connectPrevCorner || connectNextCorner) {
            // 4. Retrieve neighbour facades points:
            const prevIdx = (idx - 1 + this.perimeter.length) % this.perimeter.length;
            const nextIdx = idx2;

            const prevA = this.perimeter[prevIdx];
            const prevB = this.perimeter[idx];
            const nextA = this.perimeter[nextIdx];
            const nextB = this.perimeter[(nextIdx + 1) % this.perimeter.length];

            // 5. Compute neighbour facades "freedom":
            const testPrevA = new FVector(prevA.x + avgDirXY.x, prevA.y + avgDirXY.y);
            const testPrevB = new FVector(prevB.x + avgDirXY.x, prevB.y + avgDirXY.y);
            const testNextA = new FVector(nextA.x + avgDirXY.x, nextA.y + avgDirXY.y);
            const testNextB = new FVector(nextB.x + avgDirXY.x, nextB.y + avgDirXY.y);

            const prevFree = !this.visibilityLoops.some((loop) => isPointInPolygon(testPrevA, loop) || isPointInPolygon(testPrevB, loop));
            const nextFree = !this.visibilityLoops.some((loop) => isPointInPolygon(testNextA, loop) || isPointInPolygon(testNextB, loop));
            const prevRendered = this.renderedIndices.has((this.edgeIndex - 1 + this.perimeter.length) % this.perimeter.length);
            const nextRendered = this.renderedIndices.has((this.edgeIndex + 1) % this.perimeter.length);
            const prevConnectable = connectPrevCorner && (prevRendered || prevFree);
            const nextConnectable = connectNextCorner && (nextRendered || nextFree);

            // 6. Compute neighbours facade pushout points if they are free (and use offset if needed):
            const [prevOffsettedA, prevOffsettedB] = prevConnectable ? this.getOffsetEdge(prevA, prevB) : [null, null];
            const [nextOffsettedA, nextOffsettedB] = nextConnectable ? this.getOffsetEdge(nextA, nextB) : [null, null];
            const prevBaseA = prevOffsettedA ? new THREE.Vector2(prevOffsettedA.x, prevOffsettedA.y) : null;
            const prevBaseB = prevOffsettedB ? new THREE.Vector2(prevOffsettedB.x, prevOffsettedB.y) : null;
            const nextBaseA = nextOffsettedA ? new THREE.Vector2(nextOffsettedA.x, nextOffsettedA.y) : null;
            const nextBaseB = nextOffsettedB ? new THREE.Vector2(nextOffsettedB.x, nextOffsettedB.y) : null;

            const [prevOutA3D, prevOutB3D] =
                prevConnectable && prevOffsettedA && prevOffsettedB
                    ? this.computePushOutForEdge(prevOffsettedA, prevOffsettedB, prevA, prevB)
                    : [null, null];
            const [nextOutA3D, nextOutB3D] =
                nextConnectable && nextOffsettedA && nextOffsettedB
                    ? this.computePushOutForEdge(nextOffsettedA, nextOffsettedB, nextA, nextB)
                    : [null, null];
            const prevOutA = prevOutA3D ? new THREE.Vector2(prevOutA3D.x, prevOutA3D.y) : null;
            const prevOutB = prevOutB3D ? new THREE.Vector2(prevOutB3D.x, prevOutB3D.y) : null;
            const nextOutA = nextOutA3D ? new THREE.Vector2(nextOutA3D.x, nextOutA3D.y) : null;
            const nextOutB = nextOutB3D ? new THREE.Vector2(nextOutB3D.x, nextOutB3D.y) : null;

            // 7. Connected Corners: Compute intersections with free neighbors & update current facade pushout points:
            const currBaseA = new THREE.Vector2(p1Base.x, p1Base.y);
            const currBaseB = new THREE.Vector2(p2Base.x, p2Base.y);
            const currOutA = new THREE.Vector2(p1Out.x, p1Out.y);
            const currOutB = new THREE.Vector2(p2Out.x, p2Out.y);

            // 7.1. Update the outter edge points if connect is true and we have neighbours:
            const prevOutIntersection = this.intersectLines(prevConnectable, prevOutA, prevOutB, currOutA, currOutB);
            if (prevOutIntersection) p1Out.set(prevOutIntersection.x, prevOutIntersection.y, p1Out.z);
            const nextOutIntersection = this.intersectLines(nextConnectable, nextOutA, nextOutB, currOutA, currOutB);
            if (nextOutIntersection) p2Out.set(nextOutIntersection.x, nextOutIntersection.y, p2Out.z);

            // 7.2. Exclusively for offsetted base points, if connect is true and we have neighbours, update the base points:
            const prevBaseIntersection = this.intersectLines(prevConnectable, prevBaseA, prevBaseB, currBaseA, currBaseB);
            if (prevBaseIntersection) p1Base.set(prevBaseIntersection.x, prevBaseIntersection.y, p1Base.z);
            const nextBaseIntersection = this.intersectLines(nextConnectable, nextBaseA, nextBaseB, currBaseA, currBaseB);
            if (nextBaseIntersection) p2Base.set(nextBaseIntersection.x, nextBaseIntersection.y, p2Base.z);
        }

        // 8. Build panel with updated (or not) pushout points:
        const basePoints = [p1Base, p2Base, new FVector(p2Out.x, p2Out.y, p2Out.z), new FVector(p1Out.x, p1Out.y, p1Out.z)];

        const geom = new THREE.BufferGeometry().setFromPoints(basePoints.map((p) => new THREE.Vector3(p.x, p.y, p.z)));
        geom.setIndex([0, 1, 2, 0, 2, 3]);
        geom.computeVertexNormals();

        const mesh = new THREE.Mesh(geom, this.material);
        const edges = new THREE.LineSegments(new THREE.EdgesGeometry(geom, 0.01), PvPanelsComponent.edgeMaterial);
        shadingGroup.add(mesh, edges);

        if (this.closed) {
            const wallP1 = new FVector(p1Base.x, p1Base.y, p1Out.z);
            const wallP2 = new FVector(p2Base.x, p2Base.y, p2Out.z);
            this.addFace(
                shadingGroup,
                [wallP1, wallP2, new FVector(p2Out.x, p2Out.y, p2Out.z), new FVector(p1Out.x, p1Out.y, p1Out.z)],
                [0, 1, 2, 0, 2, 3]
            );
            this.addFace(shadingGroup, [p1Base, wallP1, new FVector(p1Out.x, p1Out.y, p1Out.z)], [0, 1, 2]);
            this.addFace(shadingGroup, [p2Base, new FVector(p2Out.x, p2Out.y, p2Out.z), wallP2], [0, 1, 2]);
        }

        this.syncFacadeConfig();
        return shadingGroup;
    }
}
